// The user's Anthropic API key: a read-only row on the account page
// (`/account`, the twin of `GmailConnection`). Only an admin assigns a key
// (`/admin/keys`); assigned, the server's model calls for this user run on
// it (`apps/server/src/http/anthropic-key.ts`).
//
// Everything it shows comes from `GET /api/anthropic-key/status`:
//
//   - nothing     a 404 (an env where no key is assigned and the user has
//                 none, `onAvailability("off")`), or any other failed read
//                 (`"failed"`, so the page can say so).
//   - none        no key an admin assigned: the status line only.
//   - assigned    the status line, then the key's last 30 days from
//                 `GET /api/anthropic-key/spend` (the total and its split
//                 by surface, or the one surface's name); read before the
//                 row reports `"shown"`, so it never arrives later; no line
//                 on a failed read or a null answer.
//
// The status says only `connected` and `assigned`, never the key; a row
// that is not assigned pays for no call, so it reads as none.

import { useEffect, useId, useRef, useState } from "react";

import {
  AnthropicKeySpendResponseSchema,
  AnthropicKeyStatusSchema,
  type AnthropicKeySpend,
} from "@loom/shared/admin";

import { formatUsdDisplay } from "../analytics/usd";
import type { ConnectionAvailability } from "../components/GitHubConnection";
import { useLocale, useT, type MessageKey } from "../i18n/index.js";
import { httpGet, HttpError } from "../lib/http";

type View = "loading" | "hidden" | "none" | "assigned";

const SURFACES: ReadonlyArray<readonly [keyof AnthropicKeySpend["bySurface"], MessageKey]> = [
  ["coding", "analytics.cost.surface.coding"],
  ["pm", "analytics.cost.surface.pm"],
  ["handoff", "analytics.cost.surface.handoff"],
  ["workflow", "analytics.cost.surface.workflow"],
];

export function AnthropicKeyConnection({
  onAvailability,
}: {
  /** Once per settled read: `"off"` on a 404, `"failed"` on an error,
   *  `"shown"` when the row renders. */
  readonly onAvailability?: (availability: ConnectionAvailability) => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const onAvailabilityRef = useRef(onAvailability);
  onAvailabilityRef.current = onAvailability;
  const [view, setView] = useState<View>("loading");
  const [spend, setSpend] = useState<AnthropicKeySpend | null>(null);
  const headId = useId();

  useEffect(() => {
    let alive = true;
    void (async () => {
      let next: View;
      let availability: ConnectionAvailability;
      try {
        const status = await httpGet("/api/anthropic-key/status", AnthropicKeyStatusSchema);
        next = status.assigned ? "assigned" : "none";
        availability = "shown";
      } catch (err) {
        next = "hidden";
        availability = err instanceof HttpError && err.status === 404 ? "off" : "failed";
      }
      // The spend is read before the row reports, so the page shows it once,
      // already whole.
      let figure: AnthropicKeySpend | null = null;
      if (alive && next === "assigned") {
        try {
          figure = (await httpGet("/api/anthropic-key/spend", AnthropicKeySpendResponseSchema))
            .spend;
        } catch {
          // No figure: the status line stands alone.
        }
      }
      if (!alive) return;
      setView(next);
      setSpend(figure);
      onAvailabilityRef.current?.(availability);
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (view === "loading" || view === "hidden") return null;

  const usd = (value: string): string => formatUsdDisplay(value, locale);
  // Two or more surfaces: each with its amount. One: its name alone, since its
  // amount is the total. None: the total alone.
  const spent =
    spend === null ? [] : SURFACES.filter(([surface]) => Number(spend.bySurface[surface]) !== 0);
  const split =
    spend === null
      ? ""
      : spent.length === 1
        ? t(spent[0]![1])
        : spent
            .map(([surface, label]) => `${t(label)} ${usd(spend.bySurface[surface])}`)
            .join(", ");

  return (
    <section className="account-row" aria-labelledby={headId}>
      <h3 id={headId} className="account-row-title">
        {t("account.anthropic.title")}
      </h3>
      <p className="account-row-status">
        {t(view === "assigned" ? "account.anthropic.assigned" : "account.anthropic.none")}
      </p>
      {view === "assigned" && spend !== null ? (
        <p className="account-row-status">
          {split === ""
            ? t("account.anthropic.spend", { total: usd(spend.totalUsd) })
            : t("account.anthropic.spendSplit", { total: usd(spend.totalUsd), split })}
        </p>
      ) : null}
    </section>
  );
}
